/**
 * Pattern product cards
 *
 * Only runs when the section is set to carousel mode; the grid layout is pure
 * CSS. Swiper is loaded once globally in layout/theme.liquid, so this waits
 * for the vendored bundle instead of loading its own copy.
 */
class PatternProductCards extends HTMLElement {
  connectedCallback() {
    // The theme editor can move a section without disconnecting it first;
    // without this guard the listeners and Swiper instance stack up.
    if (this.initialized) return;
    this.initialized = true;

    if (this.dataset.layout !== 'carousel') return;

    this.swiperEl = this.querySelector('.swiper');
    this.slides = this.querySelectorAll('.swiper-slide');
    if (!this.swiperEl || this.slides.length === 0) return;

    this.waitFor(
      () => window.Swiper,
      () => this.init()
    );
  }

  init() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = this.dataset.autoplay === 'true' && !reduceMotion;
    const autoplayDelay = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
    const gap = parseInt(this.dataset.gap, 10) || 20;
    const desktopSlides = parseFloat(this.dataset.slidesDesktop) || 3;
    const tabletSlides = parseFloat(this.dataset.slidesTablet) || 2;
    const mobileSlides = parseFloat(this.dataset.slidesMobile) || 1.1;
    const paginationEl = this.querySelector('.ppc__pagination');

    this.swiper = new window.Swiper(this.swiperEl, {
      slidesPerView: mobileSlides,
      spaceBetween: gap,
      speed: 500,
      // Looping needs more slides than fit on screen, or Swiper duplicates
      // cards into gaps and the arrows stop making sense.
      loop: this.dataset.loop === 'true' && this.slides.length > desktopSlides,
      a11y: { enabled: true },
      keyboard: { enabled: true },
      autoplay: autoplayEnabled
        ? { delay: autoplayDelay, pauseOnMouseEnter: true, disableOnInteraction: false }
        : false,
      navigation: {
        prevEl: this.querySelector('.ppc__arrow--prev'),
        nextEl: this.querySelector('.ppc__arrow--next'),
        disabledClass: 'swiper-button-disabled'
      },
      pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
      breakpoints: {
        750: { slidesPerView: tabletSlides },
        990: { slidesPerView: desktopSlides }
      }
    });
  }

  waitFor(check, callback, attempts) {
    attempts = attempts || 0;
    if (check()) return callback();
    if (attempts > 100) return;
    setTimeout(() => this.waitFor(check, callback, attempts + 1), 50);
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.initialized = false;
  }
}

if (!customElements.get('pattern-product-cards')) {
  customElements.define('pattern-product-cards', PatternProductCards);
}
